import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, Calendar, Plus, ArrowUpDown } from "lucide-react";
import CapCorner from "@/components/CapCorner";

const HEADSHOT_FALLBACK = "https://a.espncdn.com/i/headshots/nfl/players/full/0.png";

const fmtMoney = (m) => {
  if (m == null || isNaN(m)) return "—";
  if (m >= 1000) return `$${(m / 1000).toFixed(2)}B`;
  return `$${Number(m).toFixed(2)}M`;
};

const SORTS = [
  { key: "total_value", label: "Total $", desc: true },
  { key: "avg_per_year", label: "AAV", desc: true },
  { key: "cap_hit", label: "Cap Hit", desc: true },
  { key: "guaranteed", label: "Guaranteed", desc: true },
  { key: "years_left", label: "Years Left", desc: true },
];

export default function ContractHub({ embedded = false }) {
  const [contracts, setContracts] = useState([]);
  const [headshots, setHeadshots] = useState({}); // player_id -> {headshot, fullName, position, jersey}
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("total_value");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, r] = await Promise.all([api.get("/contracts"), api.get("/roster")]);
        setContracts(c.data.contracts || []);
        const map = {};
        for (const a of r.data.athletes || []) {
          map[a.id] = { headshot: a.headshot, fullName: a.fullName, position: a.position, jersey: a.jersey };
        }
        setHeadshots(map);
      } catch {
        toast.error("Failed to load contract data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totals = useMemo(() => {
    const t = contracts.reduce(
      (a, c) => ({
        total: a.total + (c.total_value || 0),
        cap: a.cap + (c.cap_hit || 0),
        gtd: a.gtd + (c.guaranteed || 0),
        aav: a.aav + (c.avg_per_year || 0),
      }),
      { total: 0, cap: 0, gtd: 0, aav: 0 }
    );
    return t;
  }, [contracts]);

  const display = useMemo(() => {
    let arr = [...contracts];
    if (query) {
      const q = query.toLowerCase();
      arr = arr.filter(
        (c) =>
          (c.full_name || "").toLowerCase().includes(q) ||
          (c.position || "").toLowerCase().includes(q)
      );
    }
    arr.sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
    return arr;
  }, [contracts, sortKey, query]);

  return (
    <div data-testid="contract-hub">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue inline-flex items-center gap-1">
            <DollarSign size={12} /> Contract Hub
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-wide section-title">The Books</h2>
          <p className="text-titans-silver mt-3 max-w-2xl font-body">
            Every Titan, every dollar — money, length, guaranteed cash, and years remaining on each deal.
          </p>
        </div>
        <Link
          to="/admin"
          data-testid="contract-admin-link"
          className="self-start md:self-auto border-2 border-titans-blue text-titans-blue hover:bg-titans-blue hover:text-white px-5 py-3 font-cond uppercase tracking-wider text-sm inline-flex items-center gap-2 transition-colors"
        >
          <Plus size={14} /> Manage Contracts
        </Link>
      </div>

      <div className="mb-8">
        {embedded ? null : <CapCorner variant="wide" />}
      </div>

      {/* TOTALS BAR */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="contract-totals">
        {[
          { label: "Roster Total", value: totals.total, cls: "flag-card-blue", text: "text-white", sub: "text-white/85" },
          { label: "Annual AAV Sum", value: totals.aav, cls: "flag-card-white", text: "text-titans-navy", sub: "text-titans-navy/70" },
          { label: "Current Cap Hit", value: totals.cap, cls: "flag-card-red", text: "text-white", sub: "text-white/85" },
          { label: "Guaranteed Out", value: totals.gtd, cls: "titans-card", text: "text-titans-blue", sub: "text-titans-silver" },
        ].map((m) => (
          <div key={m.label} className={`${m.cls} p-5`}>
            <div className={`font-cond uppercase tracking-[0.25em] text-[10px] ${m.sub}`}>{m.label}</div>
            <div className={`stat-number ${m.text} mt-1`} style={{ fontSize: "clamp(2rem, 5vw, 3rem)" }}>
              {fmtMoney(m.value)}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between mb-5">
        <div className="flex gap-2 flex-wrap">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSortKey(s.key)}
              data-testid={`contract-sort-${s.key}`}
              className={`font-cond uppercase tracking-wider text-[11px] px-3 py-1.5 border inline-flex items-center gap-1 transition-colors ${
                sortKey === s.key
                  ? "bg-titans-red border-titans-red text-white"
                  : "border-[rgba(138,141,143,0.3)] text-titans-silver hover:text-white hover:border-white"
              }`}
            >
              <ArrowUpDown size={10} /> {s.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search player or position…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="contract-search"
          className="bg-titans-navy border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-4 py-2.5 text-white font-body w-full md:w-72"
        />
      </div>

      {loading ? (
        <div className="text-titans-silver">Loading contracts…</div>
      ) : contracts.length === 0 ? (
        <div className="titans-card p-10 text-center">
          <DollarSign size={36} className="mx-auto text-titans-red" />
          <div className="font-display text-3xl uppercase tracking-wide mt-4">No contracts on file yet.</div>
          <div className="text-titans-silver mt-2 font-body">
            Add your first player contract from the Owner Console.
          </div>
          <Link
            to="/admin"
            className="mt-6 inline-flex items-center gap-2 bg-titans-red hover:bg-titans-red-hover px-6 py-3 font-cond uppercase tracking-wider text-sm"
          >
            <Plus size={14} /> Open Owner Console
          </Link>
        </div>
      ) : (
        <div className="titans-card overflow-x-auto" data-testid="contract-table">
          <table className="w-full min-w-[900px]">
            <thead className="bg-titans-elev">
              <tr className="text-left font-cond uppercase tracking-wider text-[10px] text-titans-silver">
                <th className="px-4 py-3">Player</th>
                <th className="px-4 py-3">Pos</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">AAV</th>
                <th className="px-4 py-3 text-right">Guaranteed</th>
                <th className="px-4 py-3 text-right">Cap Hit</th>
                <th className="px-4 py-3 text-right">Years</th>
                <th className="px-4 py-3 text-right">Yrs Left</th>
                <th className="px-4 py-3">Expires</th>
              </tr>
            </thead>
            <tbody>
              {display.map((c, i) => {
                const meta = headshots[c.player_id] || {};
                return (
                  <tr
                    key={c.player_id}
                    data-testid={`contract-row-${i}`}
                    className="border-t border-[rgba(138,141,143,0.15)] hover:bg-titans-elev/40"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="relative h-10 w-10 bg-titans-elev shrink-0 overflow-hidden">
                          <img
                            src={meta.headshot || HEADSHOT_FALLBACK}
                            alt={c.full_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.src = HEADSHOT_FALLBACK; }}
                          />
                        </div>
                        <div>
                          <div className="font-display text-base uppercase leading-tight text-white">{c.full_name}</div>
                          <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
                            #{c.jersey || meta.jersey || "--"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-cond uppercase tracking-wider text-titans-blue">{c.position || meta.position || "—"}</td>
                    <td className="px-4 py-3 text-right font-display text-lg text-white">{fmtMoney(c.total_value)}</td>
                    <td className="px-4 py-3 text-right font-body text-titans-silver">{fmtMoney(c.avg_per_year)}</td>
                    <td className="px-4 py-3 text-right font-body text-titans-silver">{fmtMoney(c.guaranteed)}</td>
                    <td className="px-4 py-3 text-right font-body text-titans-red">{fmtMoney(c.cap_hit)}</td>
                    <td className="px-4 py-3 text-right font-body text-titans-silver">{c.length_years || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 font-cond uppercase tracking-wider text-xs text-titans-blue">
                        <Calendar size={11} /> {c.years_left ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-body text-titans-silver">{c.expires_year || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
